import { useEffect, useRef, useState } from 'react';
import { AUTH_COPY, type AuthError } from '../lib/auth.ts';
import { googleClientId, loadGis, makeNonce } from '../lib/google.ts';
import { useRepo } from '../lib/hooks.ts';

/** "Continue with Google" (ADR-0004). Renders nothing unless a client id is configured and the device is online. */
export function GoogleButton() {
  const repo = useRepo();
  const box = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<AuthError | 'unavailable' | null>(null);
  const clientId = googleClientId();
  const online = typeof navigator === 'undefined' || navigator.onLine !== false;
  const signIn = repo.auth?.signInWithGoogle?.bind(repo.auth);
  useEffect(() => {
    if (!clientId || !online || !signIn || !box.current) return;
    let alive = true;
    void (async () => {
      try {
        const [gis, nonce] = await Promise.all([loadGis(), makeNonce()]);
        if (!alive || !box.current) return;
        gis.initialize({
          client_id: clientId,
          nonce: nonce.hashed,
          use_fedcm_for_prompt: true,
          ux_mode: 'popup',
          callback: async ({ credential }) => {
            if (!credential) return setError('unknown');
            const r = await signIn(credential, nonce.raw);
            if (!r.ok) return setError(r.error);
            await repo.signedIn(r.user);
          },
        });
        gis.renderButton(box.current, { type: 'standard', theme: 'filled_black', size: 'large', text: 'continue_with', shape: 'pill', width: 320 });
      } catch {
        if (alive) setError('unavailable');
      }
    })();
    return () => {
      alive = false;
    };
  }, [clientId, online, signIn, repo]);
  if (!clientId || !signIn || !online) return null;
  return (
    <div className="mt-6">
      <div ref={box} className="min-h-12 flex justify-center" data-testid="google-button" />
      {error && (
        <p className="text-miss text-sm mt-2" role="alert">
          {error === 'unavailable' ? "Google sign-in isn't available right now. Use your email instead." : AUTH_COPY[error]}
        </p>
      )}
      <p className="text-center text-sm text-text-muted mt-4">or use your email</p>
    </div>
  );
}
